'use client';

import { useId, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useLocale, useTranslation } from './LocaleProvider';
import { MONO_FONT } from '@/theme';

const OPTIONS = [
  { value: 'es', code: 'ES', name: 'Español' },
  { value: 'en', code: 'EN', name: 'English' },
  { value: 'pt-BR', code: 'PT', name: 'Português' },
];

// Sigla del idioma activo con una flecha pequeña; al abrirlo, un menú propio (no la lista del
// navegador) con el nombre completo de cada idioma. Se abre hacia arriba: vive en el pie.
export default function LanguageSelector({ dark = false }) {
  const { locale, setLocale, ready } = useLocale();
  const t = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const menuId = useId();
  const open = Boolean(anchor);
  const current = OPTIONS.find((option) => option.value === locale) ?? OPTIONS[0];

  const choose = (value) => {
    setAnchor(null);
    if (value !== locale) setLocale(value);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={!ready}
        aria-label={`${t('Idioma')}: ${current.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          p: 0,
          border: 0,
          background: 'none',
          fontFamily: MONO_FONT,
          fontSize: '10.5px',
          letterSpacing: '0.12em',
          color: dark ? 'grey.400' : 'text.muted',
          flexShrink: 0,
          cursor: 'pointer',
          transition: 'color 0.15s',
          '&:hover, &[aria-expanded="true"]': { color: dark ? 'common.white' : 'text.primary' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 4,
            borderRadius: '2px',
          },
          '&:disabled': { cursor: 'default' },
        }}
      >
        {current.code}
        <Box
          component="svg"
          aria-hidden="true"
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
          sx={{
            flexShrink: 0,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <path
            d="M1 2.5 4 5.5 7 2.5"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        disableScrollLock
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transitionDuration={120}
        slotProps={{
          paper: {
            sx: {
              mt: -1.5,
              minWidth: 148,
              borderRadius: '2px',
              boxShadow: 'none',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: dark ? 'var(--ink-700)' : 'divider',
              bgcolor: dark ? 'var(--ink-900)' : 'background.paper',
            },
          },
          list: { sx: { py: 0.5 } },
        }}
      >
        {OPTIONS.map((option) => {
          const selected = option.value === locale;
          return (
            <MenuItem
              key={option.value}
              role="menuitemradio"
              aria-checked={selected}
              selected={selected}
              lang={option.value}
              onClick={() => choose(option.value)}
              sx={{
                justifyContent: 'space-between',
                gap: 2,
                minHeight: 36,
                px: 2,
                fontFamily: MONO_FONT,
                fontSize: '12px',
                letterSpacing: '0.04em',
                color: dark ? 'var(--ink-300)' : 'text.primary',
                '&:hover, &.Mui-focusVisible': {
                  bgcolor: dark ? 'var(--ink-800)' : 'action.hover',
                  color: dark ? 'common.white' : 'text.primary',
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'transparent',
                  color: dark ? 'var(--teal-glow)' : 'text.accent',
                },
                '&.Mui-focusVisible': { outline: 'none' },
              }}
            >
              {option.name}
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                  visibility: selected ? 'visible' : 'hidden',
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
